import { Info } from "@mui/icons-material";
import { Box, Grid, TextField, Typography, useTheme } from "@mui/material";
import { FormikProps } from "formik";
import { FC, useEffect, useState } from "react";
import { Country, WidgetInput } from "react-addressfinder";
import { CardOfferingType } from "../../models/employer/cardOfferingType";
import { useConfig } from "../../utils/useConfig";
import { useData } from "../../utils/useData";
import "../AddressFinderForm/AddressFinderForm.css";
import { PersonalDetailsFormValuesType } from "../PersonalDetailsForm";

export const AddressFinderForm: FC<{
  formik: FormikProps<PersonalDetailsFormValuesType>;
  countryCode?: string;
}> = ({ formik, countryCode }) => {
  const theme = useTheme();
  const { config } = useConfig();
  const { offerings } = useData();

  const [hasDigitalOffering, setHasDigitalOffering] = useState(false);

  useEffect(() => {
      offerings.data?.forEach((offering) => {
        if (offering.type === CardOfferingType.DIGITAL)
          setHasDigitalOffering(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerings.data]);

  const [isCantFindAddress, setIsCantFindAddress] = useState(false);

  const resolveCountry = () => {
    switch (countryCode) {
      case "NZ":
        return Country.NZ;
      case "AU":
        return Country.AU;
    }
  };

  const updateAddress = (
    address1: string,
    address2: string,
    city: string,
    stateOrProvince: string,
    postalCode: string
  ) => {
    formik.setFieldValue("address1", address1);
    formik.setFieldValue("address2", address2);
    formik.setFieldValue("city", city);
    formik.setFieldValue("stateOrProvince", stateOrProvince);
    formik.setFieldValue("postalCode", postalCode);
  };

  // Address type is badly defined in react package, hence the "any"
  const handlePlaceSelected = async (address: any) => {
    let line2 = address.address_line_2 || "";

    const isSuburbNonEmpty = address?.suburb && address?.suburb?.length > 0;
    const isNotAustralia = address.country !== "AU";
    const isLine2NonEmpty =
      address?.address_line_2 && address?.address_line_2?.length > 0;

    if (isSuburbNonEmpty && isNotAustralia) {
      line2 = isLine2NonEmpty
        ? `${address.address_line_2}, ${address.suburb}`
        : address.suburb;
    }

    // For NZ addresses, append the rd_number (rural road number), if provided
    if (address?.rd_number) {
      line2 = `${line2} RD ${address.rd_number}`;
    }

    updateAddress(
      address?.postal_line_1 ?? address?.address_line_1,
      line2,
      address?.mailtown ??
        address?.city ??
        address?.suburb ??
        address?.locality_name,
      address?.state_territory ?? "",
      address?.postcode
    );
  };

  const clearAddress = () => {
    updateAddress("", "", "", "", "");
  };

  return (
    <>
      <Grid item xs={11.7}>
        <Typography mb={2} mt={3}>
          Postal Address{" "}
          {!hasDigitalOffering && (
            <span>(this is where your card will be sent)</span>
          )}
        </Typography>
        <WidgetInput
          name="address1"
          value={formik.values.address1}
          country={resolveCountry()}
          addressFinderKey={config?.ADDRESS_FINDER_KEY ?? ""}
          inputClassName="addressFinder"
          id="Address"
          raw={true}
          onSelected={(fullAddress: string, address: any) => {
            handlePlaceSelected(address);
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.value !== formik.values.address1) {
              clearAddress(); // If input changes clear address, so they can't edit a found address
            }
            formik.setFieldValue("address1", e.target.value);
          }}
          placeholder="Address 1"
          required
          style={{
            width: "100%",
            paddingTop: 10,
            paddingBottom: 10,
            paddingLeft: 10,
            fontSize: 16,
            outlineColor: theme.palette.primary.main,
            borderColor: "lightgray",
            borderStyle: "solid",
            borderWidth: 1,
            borderRadius: 3,
            backgroundColor: "transparent"
          }}
          addressParams={{ au_paf: "1", delivered: "1" }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          disabled={true}
          value={formik.values.address2}
          onChange={formik.handleChange}
          name="address2"
          error={formik.touched.address2 && Boolean(formik.errors.address2)}
          helperText={formik.touched.address2 && formik.errors.address2}
          label="Address 2"
          fullWidth
          size="small"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          disabled={true}
          value={formik.values.city}
          onChange={formik.handleChange}
          name="city"
          error={formik.touched.city && Boolean(formik.errors.city)}
          helperText={formik.touched.city && formik.errors.city}
          label="City"
          fullWidth
          size="small"
          required={true}
        />
      </Grid>
      {countryCode &&
        countryCode !== "NZ" && ( // Only show state/province if not in NZ
          <Grid item xs={12}>
            <TextField
              disabled={true}
              value={formik.values.stateOrProvince}
              onChange={formik.handleChange}
              name="stateOrProvince"
              error={
                formik.touched.stateOrProvince &&
                Boolean(formik.errors.stateOrProvince)
              }
              helperText={
                formik.touched.stateOrProvince && formik.errors.stateOrProvince
              }
              label="State or Province"
              fullWidth
              size="small"
              required={formik.values.country !== "NZ"}
            />
          </Grid>
        )}
      <Grid item xs={12}>
        <TextField
          disabled={true}
          value={formik.values.postalCode}
          onChange={formik.handleChange}
          name="postalCode"
          error={formik.touched.postalCode && Boolean(formik.errors.postalCode)}
          helperText={formik.touched.postalCode && formik.errors.postalCode}
          label="Postal Code"
          required
          fullWidth
          size="small"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          value={formik.values.country}
          onChange={formik.handleChange}
          name="country"
          label="Country"
          required
          fullWidth
          size="small"
          disabled={true}
        />
      </Grid>
      <Grid item xs={12}>
        <Box
          display="flex"
          gap={0.5}
          sx={{ cursor: "pointer" }}
          onClick={() => setIsCantFindAddress(!isCantFindAddress)}
        >
          <Info color="primary" fontSize="small" />
          <Typography
            color="primary"
            fontSize={14}
            sx={{ textDecoration: "underline" }}
          >
            I can't find my address?
          </Typography>
        </Box>

        {isCantFindAddress && (
          <>
            <Typography my={1} fontSize={14} color="secondary">
              We only list verified postal addresses. If your address isn't
              found, reach out to{" "}
              <a
                style={{ color: theme.palette.primary.main }}
                href="mailto:support@healthnowhq.com"
              >
                support@healthnowhq.com
              </a>
              .
            </Typography>
          </>
        )}
      </Grid>
    </>
  );
};
